import {
  Checkbox as MantineCheckbox,
  CheckboxProps as MantineCheckboxProps,
} from '@mantine/core';
import { forwardRef } from 'react';

import styles from './Checkbox.module.css';

export interface CheckboxProps extends Omit<
  MantineCheckboxProps,
  'variant' | 'color' | 'styles' | 'classNames' | 'style' | 'size' | 'readOnly'
> {
  /** @deprecated Use `checked` instead. Preserved for V1 compatibility. */
  isChecked?: boolean;
  /** Adds bordered container styling around checkbox */
  withBorder?: boolean;
  /** Adds fs-mask class to label for FullStory privacy masking */
  isMasked?: boolean;
  /** Makes unchecked checkbox background transparent */
  isTransparent?: boolean;
  isReadOnly?: boolean;
  /** Whether to truncate overflowing text with an ellipsis */
  shouldTruncate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      isChecked,
      checked,
      withBorder = false,
      isMasked = false,
      isTransparent = false,
      isReadOnly = false,
      shouldTruncate,
      label,
      ...props
    },
    ref
  ) => {
    // Support both isChecked (V1) and checked (standard) props
    const isControlled = isChecked !== undefined || checked !== undefined;
    const checkedValue = isChecked ?? checked;

    // Build label class names with optional fs-mask
    const labelClassName = isMasked ? `${styles.label} fs-mask` : styles.label;

    return (
      <MantineCheckbox
        ref={ref}
        unstyled
        checked={isControlled ? checkedValue : undefined}
        label={label}
        classNames={{
          root: styles.root,
          input: styles.input,
          icon: styles.icon,
          inner: styles.inner,
          body: styles.body,
          labelWrapper: styles.labelWrapper,
          label: labelClassName,
          description: styles.description,
          error: styles.error,
        }}
        wrapperProps={{
          'data-with-border': withBorder,
          'data-readonly': isReadOnly,
          'data-checked': checkedValue,
          'data-transparent': isTransparent,
          'data-truncate': shouldTruncate || undefined,
        }}
        {...props}
      />
    );
  }
);

Checkbox.displayName = 'Checkbox';
